import type { Option } from '../../../../components/DataTable/DataTable.types';

import { Button } from '../../../../components/Button';
import { ToggleGroup } from '../../../../components/ToggleGroup';

const BOOLEAN_FILTER_VALUES = ['true', 'false'] as const;

type BooleanFilterValue = (typeof BOOLEAN_FILTER_VALUES)[number];

interface DataTableBooleanFilterProps {
  title?: string;
  options: Option[];
  selectedValues: Set<string>;
  onSelectValue: (option: Option) => void;
  onClear: () => void;
}

export const isBooleanFilterOptions = (options: Option[]) => {
  const values = new Set(options.map((option) => String(option.value)));

  return options.length === 2 && values.has('true') && values.has('false');
};

export function DataTableBooleanFilter({
  title,
  options,
  selectedValues,
  onSelectValue,
  onClear,
}: DataTableBooleanFilterProps) {
  const trueOption = options.find((option) => String(option.value) === 'true');
  const falseOption = options.find((option) => String(option.value) === 'false');

  const selectedValue: BooleanFilterValue | undefined =
    selectedValues.size === 1 && selectedValues.has('true')
      ? 'true'
      : selectedValues.size === 1 && selectedValues.has('false')
        ? 'false'
        : undefined;

  const labelByValue: Record<BooleanFilterValue, string | undefined> = {
    true: trueOption?.label,
    false: falseOption?.label,
  };

  const selectByValue: Record<BooleanFilterValue, () => void> = {
    true: () => trueOption && onSelectValue(trueOption),
    false: () => falseOption && onSelectValue(falseOption),
  };

  const items: { label: string; value: BooleanFilterValue }[] = BOOLEAN_FILTER_VALUES.flatMap((value) => {
    const label = labelByValue[value];

    return label ? [{ label, value }] : [];
  });

  const handleValueChange = (value: string) => {
    const booleanValue = BOOLEAN_FILTER_VALUES.find((filterValue) => filterValue === value);

    if (booleanValue) selectByValue[booleanValue]();
  };

  return (
    <div className="space-y-2">
      <ToggleGroup
        className="h-auto w-full justify-start"
        name={title}
        options={items}
        value={selectedValue}
        onChange={handleValueChange}
      />
      {selectedValue && (
        <Button variant="ghost" size="sm" className="h-8 w-full justify-center" onClick={onClear}>
          Clear filter
        </Button>
      )}
    </div>
  );
}
